/**
 * Categories Mobile - Drill-down Navigation
 * Handles 3-level category navigation with smooth transitions
 * L1 = main categories, L2 = subcategories, L3 = sub-subcategories (lazy-loaded)
 * All levels use full-width banner cards
 */

class CategoriesMobile {
  constructor(container) {
    this.container = container;
    this.levels = container.querySelectorAll('.categories-mobile__level');
    this.history = [];
    this.level3Data = null;
    this.config = null;
    this.level3Cache = {};

    this.loadConfig();
    this.setViewportHeight();
    this.bindEvents();
    this.bindResizeEvents();
    this.loadLevel3Cache();
    this.dedupAllLevel2().catch(() => {});
  }

  /**
   * Dedup duplicate first-product thumbnails among siblings in one container.
   * Scope = one sibling group (a single L2/L3 panel). For each duplicate (2nd+
   * occurrence of the same image filename), fetches a few products from that
   * collection via /collections/<handle>/products.json and swaps to the first
   * product whose image isn't already used by a sibling.
   */
  async dedupImagesInContainer(container) {
    if (!container) return;
    const items = container.querySelectorAll('.categories-mobile__item');
    if (items.length < 2) return;

    const fileNameOf = (url) => {
      if (!url) return '';
      return (url.split('/').pop() || '').split('?')[0];
    };

    const fnToItems = new Map();
    for (const item of items) {
      const img = item.querySelector('img.categories-mobile__item-image');
      if (!img || !img.src) continue;
      const fn = fileNameOf(img.src);
      if (!fn) continue;
      if (!fnToItems.has(fn)) fnToItems.set(fn, []);
      fnToItems.get(fn).push({ item, img });
    }

    const usedFn = new Set(fnToItems.keys());

    for (const [, group] of fnToItems) {
      if (group.length < 2) continue;
      for (let i = 1; i < group.length; i++) {
        const { item, img } = group[i];
        const link = item.querySelector('a');
        if (!link) continue;
        const href = link.getAttribute('href') || '';
        const handle = href.split('/collections/').pop().split('/')[0].split('?')[0];
        if (!handle) continue;
        try {
          const r = await fetch(`/collections/${handle}/products.json?limit=10`, {
            headers: { 'Accept': 'application/json' }
          });
          if (!r.ok) continue;
          const j = await r.json();
          const chosen = (j.products || []).find((p) => {
            const ps = p.images && p.images[0] && p.images[0].src;
            if (!ps) return false;
            return !usedFn.has(fileNameOf(ps));
          });
          if (chosen) {
            const newSrc = chosen.images[0].src;
            usedFn.add(fileNameOf(newSrc));
            const sep = newSrc.includes('?') ? '&' : '?';
            img.src = newSrc + sep + 'width=80';
            img.removeAttribute('srcset');
          }
        } catch {
          // swallow; leaves the duplicate in place rather than breaking
        }
      }
    }
  }

  /**
   * Run dedup on every L2 panel. Each panel is its own sibling group.
   */
  async dedupAllLevel2() {
    const panels = this.container.querySelectorAll(
      '.categories-mobile__level[data-level="2"] .categories-mobile__items'
    );
    await Promise.all(
      Array.from(panels).map((p) => this.dedupImagesInContainer(p).catch(() => {}))
    );
  }

  /**
   * Load configuration and L3 data from JSON
   */
  loadConfig() {
    try {
      const dataEl = document.getElementById('categories-level3-data');
      const configEl = document.getElementById('categories-mobile-config');

      if (dataEl) {
        this.level3Data = JSON.parse(dataEl.textContent);
      }
      if (configEl) {
        this.config = JSON.parse(configEl.textContent);
      }
    } catch (e) {
      console.error('[CategoriesMobile] Failed to parse config:', e);
    }
  }

  /**
   * Load cached L3 content from sessionStorage
   */
  loadLevel3Cache() {
    try {
      const cached = sessionStorage.getItem('categories_level3_cache_v2');
      if (cached) {
        this.level3Cache = JSON.parse(cached);
      }
    } catch (e) {
      // Ignore cache errors
    }
  }

  /**
   * Save L3 cache to sessionStorage
   */
  saveLevel3Cache() {
    try {
      sessionStorage.setItem('categories_level3_cache_v2', JSON.stringify(this.level3Cache));
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Set viewport height using the CSS-Tricks proven approach
   * https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
   */
  setViewportHeight() {
    const vh = window.visualViewport
      ? window.visualViewport.height
      : window.innerHeight;

    const vhUnit = vh * 0.01;
    document.documentElement.style.setProperty('--vh', `${vhUnit}px`);
    document.documentElement.style.setProperty('--viewport-height', `${vh}px`);

    // Apply height directly to levels as final fallback for Android WebView
    this.levels.forEach(level => {
      level.style.height = `${vh}px`;
    });
  }

  bindResizeEvents() {
    let resizeTimer;
    const updateHeight = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.setViewportHeight();
      }, 150);
    };

    window.addEventListener('resize', updateHeight);

    window.addEventListener('orientationchange', () => {
      setTimeout(() => this.setViewportHeight(), 200);
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateHeight);
    }
  }

  bindEvents() {
    // L1 → L2: category open buttons
    this.container.querySelectorAll('.js-category-open').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const target = btn.dataset.target;
        if (target) {
          this.openLevel(target, 2);
        }
      });
    });

    // L2 → L3: subcategory open buttons (with lazy loading)
    this.container.querySelectorAll('.js-subcategory-open').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const target = btn.dataset.target;
        if (target) {
          this.openLevel3WithLazyLoad(target);
        }
      });
    });

    // Back buttons
    this.container.querySelectorAll('.js-category-back').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.goBack();
      });
    });

    // Handle browser back button
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.categoriesLevel !== undefined) {
        this.handlePopState(e.state);
      }
    });

    // Set initial state (L1)
    this.pushState(1, null);
  }

  /**
   * Open L3 with lazy loading
   */
  openLevel3WithLazyLoad(target) {
    const targetLevel = this.container.querySelector(
      `.categories-mobile__level[data-category="${target}"]`
    );

    if (!targetLevel) return;

    if (targetLevel.dataset.loaded !== 'true') {
      this.loadLevel3Content(target, targetLevel);
    }

    this.openLevel(target, 3);
  }

  /**
   * Load L3 content from template or JSON
   */
  loadLevel3Content(target, levelEl) {
    if (this.level3Cache[target]) {
      this.renderLevel3FromCache(target, levelEl);
      return;
    }

    const template = document.querySelector(`template[data-level3="${target}"]`);

    if (template) {
      const html = template.innerHTML;
      this.level3Cache[target] = html;
      this.saveLevel3Cache();

      const itemsList = levelEl.querySelector('.categories-mobile__items');
      if (itemsList) {
        itemsList.innerHTML = html;
        this.dedupImagesInContainer(itemsList).catch(() => {});
      }
    } else {
      this.loadLevel3FromJSON(target, levelEl);
    }

    levelEl.dataset.loaded = 'true';
  }

  /**
   * Load L3 from JSON data (list with product images)
   */
  loadLevel3FromJSON(target, levelEl) {
    const data = this.level3Data?.[target];
    if (!data) return;

    const itemsList = levelEl.querySelector('.categories-mobile__items');
    if (!itemsList) return;

    let html = '';

    if (this.config?.showViewAll) {
      html += `<li class="categories-mobile__item">
        <a href="${this.escapeHtml(data.url)}" class="categories-mobile__link">
          <span>${this.escapeHtml(this.config.viewAllText)}</span>
        </a>
      </li>`;
    }

    for (const item of data.items) {
      if (item.count === 0) continue;
      const imgSrc = item.image || '';
      const hasImg = imgSrc !== '';
      html += `<li class="categories-mobile__item">
        <a href="${this.escapeHtml(item.url)}" class="categories-mobile__link${hasImg ? ' categories-mobile__link--with-image' : ''}">
          ${hasImg ? `<img src="${this.escapeHtml(imgSrc)}" alt="${this.escapeHtml(item.title)}" class="categories-mobile__item-image" loading="lazy" width="40" height="40">` : ''}
          <span>${this.escapeHtml(item.title)}</span>
        </a>
      </li>`;
    }

    itemsList.innerHTML = html;
    this.dedupImagesInContainer(itemsList).catch(() => {});
  }

  /**
   * Render L3 from cache
   */
  renderLevel3FromCache(target, levelEl) {
    const itemsList = levelEl.querySelector('.categories-mobile__items');
    if (itemsList && this.level3Cache[target]) {
      itemsList.innerHTML = this.level3Cache[target];
      levelEl.dataset.loaded = 'true';
      this.dedupImagesInContainer(itemsList).catch(() => {});
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  openLevel(target, level) {
    const targetLevel = this.container.querySelector(
      `.categories-mobile__level[data-category="${target}"]`
    );

    if (!targetLevel) return;

    const currentLevel = this.container.querySelector('.categories-mobile__level.is-active');

    if (currentLevel) {
      currentLevel.classList.remove('is-active');
      currentLevel.classList.add('is-prev');
    }

    targetLevel.classList.add('is-active');

    this.history.push({
      level: level,
      target: target,
      previousTarget: currentLevel?.dataset.category || null
    });

    this.pushState(level, target);
    this.lockBodyScroll(true);

    targetLevel.scrollTop = 0;
    targetLevel.scrollTo({ top: 0, behavior: 'instant' });

    setTimeout(() => {
      if (currentLevel) {
        currentLevel.classList.remove('is-prev');
      }
    }, 350);
  }

  goBack() {
    if (this.history.length === 0) return;

    const current = this.history.pop();
    const currentLevel = this.container.querySelector('.categories-mobile__level.is-active');

    let targetLevel;
    if (this.history.length === 0) {
      targetLevel = this.container.querySelector('.categories-mobile__level--main');
    } else {
      const previous = this.history[this.history.length - 1];
      targetLevel = this.container.querySelector(
        `.categories-mobile__level[data-category="${previous.target}"]`
      );
    }

    if (!targetLevel) {
      targetLevel = this.container.querySelector('.categories-mobile__level--main');
    }

    if (currentLevel) {
      currentLevel.classList.remove('is-active');
      currentLevel.classList.add('is-entering');
    }

    targetLevel.classList.remove('is-prev');
    targetLevel.classList.add('is-active');

    targetLevel.scrollTop = 0;
    targetLevel.scrollTo({ top: 0, behavior: 'instant' });

    const newLevel = this.history.length > 0 ? this.history[this.history.length - 1].level : 1;
    const newTarget = this.history.length > 0 ? this.history[this.history.length - 1].target : null;
    this.pushState(newLevel, newTarget);

    if (newLevel === 1) {
      this.lockBodyScroll(false);
    }

    setTimeout(() => {
      if (currentLevel) {
        currentLevel.classList.remove('is-entering');
      }
    }, 350);
  }

  handlePopState(state) {
    const targetLevel = state.categoriesLevel;
    const targetCategory = state.categoriesTarget;

    this.levels.forEach(level => {
      level.classList.remove('is-active', 'is-prev', 'is-entering');
    });

    if (targetLevel === 1 || !targetCategory) {
      const mainLevel = this.container.querySelector('.categories-mobile__level--main');
      if (mainLevel) {
        mainLevel.classList.add('is-active');
      }
      this.history = [];
      this.lockBodyScroll(false);
    } else {
      const level = this.container.querySelector(
        `.categories-mobile__level[data-category="${targetCategory}"]`
      );
      if (level) {
        level.classList.add('is-active');
      }
      this.lockBodyScroll(true);
    }
  }

  /**
   * Lock/unlock body scroll to prevent scroll leaking on iOS
   */
  lockBodyScroll(lock) {
    if (lock) {
      this.savedScrollY = window.scrollY;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this.savedScrollY || 0);
    }
  }

  pushState(level, target) {
    const state = {
      categoriesLevel: level,
      categoriesTarget: target
    };

    if (!history.state ||
        history.state.categoriesLevel !== level ||
        history.state.categoriesTarget !== target) {
      history.pushState(state, '', window.location.href);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.categories-mobile');
  if (container) {
    const instance = new CategoriesMobile(container);

    setTimeout(() => instance.setViewportHeight(), 300);

    window.addEventListener('load', () => {
      setTimeout(() => instance.setViewportHeight(), 100);
    });
  }
});
